import React from 'react';
import PropTypes from 'prop-types';

import BEM from '@scripts/utilities/bem.js';
import CurrencyFormat from '@scripts/utilities/currency.js';


var formatters = {
  small: undefined,
  medium: undefined,
  large: undefined,
  currencysmall: undefined,
  currencymedium: undefined,
  currencylarge: undefined,
  percentsmall: undefined,
  percentmedium: undefined,
  percentlarge: undefined
};


/**
 * Return the appropriate formatter. If it does not exist, then create it and
 * cache it for later use.
 */
var getFormatter = function(formatter, formatters) {
  var result;

  // set a default
  if (! formatters.hasOwnProperty(formatter)) {
    formatter = 'medium';
  }

  // get (or create) the formatter
  if (! formatters[formatter]) {
    switch (formatter) {
      case 'small':
      case 'medium':
        result = new Intl.NumberFormat(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        );
        break;
      case 'large':
        result = new Intl.NumberFormat(
          undefined,
          { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        );
        break;
      case 'currencysmall':
      case 'currencymedium':
        try {
          result = new Intl.NumberFormat(
            undefined,
            { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }
          );
        } catch (err) {
          // Safari iPhone doesn't always like the above, we try a more basic version
          result = new Intl.NumberFormat(
            'en',
            { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }
          );
        }
        break;
      case 'currencylarge':
        try {
          result = new Intl.NumberFormat(
            undefined,
            { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 0 }
          );
        } catch (err) {
          // Safari iPhone doesn't always like the above, we try a more basic version
          result = new Intl.NumberFormat(
            'en',
            { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }
          );
        }
        break;
      case 'percentsmall':
      case 'percentmedium':
        result = new Intl.NumberFormat(
          undefined,
          { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }
        );
        break;
      case 'percentlarge':
        result = new Intl.NumberFormat(
          undefined,
          { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }
        );
        break;
    }

    // cache for next time.
    formatters[formatter] = result;
  } else {
    result = formatters[formatter];
  }

  return result;
};


/**
 * A simple component that displays a given number as a figure.
 *
 * The number will be automatically formatted based on the size of the number:
 * - small (less than 1); 3 decimal places
 * - large (greater than 100); no decimal places
 * - medium (everything else); 2 decimal places
 * this behaviour can be overridden by specifying the `formatter` property.
 */
class Figure extends React.Component {
  variant() {
    var result = [];

    if (this.props.bold) {
      result.push('bold');
    }

    return result;
  }

  render() {
    var content;

    if (! this.props.amount && this.props.zeroIsBlank) {
      content = '-';
    } else {
      content = this.formatNumber(this.props.amount, this.props.formatter);
    }

    return (
      <span className={BEM.classString('cs-figure', this.variant())}>
        {content}
      </span>
    );
  }

  formatNumber(amount, formatter) {
    if (! formatter) {
      if (Math.abs(amount) < 1) {
        formatter =  'small';
      } else if (Math.abs(amount) > 100) {
        formatter = 'large';
      } else {
        formatter = 'medium';
      }
    }

    return getFormatter(formatter, formatters).format(amount);
  }
}


Figure.propTypes = {
  amount: PropTypes.number,
  formatter: PropTypes.string,
  zeroIsBlank: PropTypes.bool,
  bold: PropTypes.bool
};

Figure.defaultProps = {
  amount: 0,
  zeroIsBlank: false,
  bold: false
};


/**
 * A simple component that displays a given number as a currency amount
 */
class CurrencyFigure extends Figure {
  formatNumber(amount, formatter) {
    if (! formatter) {
      var tmp = Math.abs(amount);
      if (tmp < 1) {
        formatter =  'small';
      } else if (tmp > 100) {
        formatter = 'large';
      } else {
        formatter = 'medium';
      }
    }

    return getFormatter('currency' + formatter, formatters).format(amount);
  }
}


class PercentFigure extends Figure {
  formatNumber(amount, formatter) {
    var prepend = '';
    var append = '%';

    if (this.props.displaySign && (amount >= 0)) {
      prepend = '+';
    }

    if (! formatter) {
      var tmp = Math.abs(amount);
      if (tmp < 1) {
        formatter =  'small';
      } else if (tmp > 100) {
        formatter = 'large';
      } else {
        formatter = 'medium';
      }
    }

    return prepend + getFormatter('percent' + formatter, formatters).format(amount) + append;
  }

  variant() {
    var result = super.variant();

    if (this.props.highlightPositive && (this.props.amount > 0)) {
      result.push('positive');
    }

    return result;
  }
}


PercentFigure.propTypes = Object.assign(
  {},
  Figure.propTypes,
  { highlightPositive: PropTypes.bool, displaySign: PropTypes.bool }
);

PercentFigure.defaultProps = Object.assign(
  {},
  Figure.defaultProps,
  { highlightPositive: true, displaySign: true }
);


export default Figure;

export { CurrencyFigure, PercentFigure };

